import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenService } from '../core/token.service';
import { Token } from './token.model';
import { User } from './user.model';
import { stringify } from 'qs';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly isAuthenticated = this.token.token.pipe(
    distinctUntilChanged(),
    map((token) => token)
  );

  readonly user = this.isAuthenticated.pipe(
    distinctUntilChanged(),
    switchMap((token) => {
      return token ? this.getCurrentUser() : of(null);
    }),
    shareReplay(1)
  );
  isAdmin = false;

  constructor(
    private http: HttpClient,
    private token: TokenService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: any
  ) {}

  getCurrentUser(): Observable<User | undefined> {
    return this.http
      .get<User>(`${environment.api}/auth/me`)
      .pipe(
        tap(me => this.isAdmin = me.roles.includes('admin')),
        catchError(() => of(undefined))
      );
  }

  curentUserHasPermission(permission: string): Observable<boolean> {
    return this.user.pipe(
      map((user) => !!user && user.permissions.includes(permission))
    );
  }

  currentUserIsAdmin(): Observable<boolean> {
    return this.user.pipe(
      map((user) => !!user && user.roles.includes('admin'))
    );
  }

  curentUserHasRole(role: string): Observable<boolean> {
    return this.user.pipe(map((user) => !!user && user.roles.includes(role)));
  }

  signIn(login: string, password: string): Observable<Token> {
    const auth = btoa(`${login}:${password}`);
    const headers = {
      Authorization: `Basic ${auth}`,
    };
    return this.http
      .get<Token>(`${environment.api}/auth/login`, { headers })
      .pipe(
        tap((token) => {
          this.token.set(token);
        })
      );
  }

  signInAndRemember(login: string, password: string): Observable<Token> {
    return this.signIn(login, password).pipe(
      tap((token) => this.token.persist(token))
    );
  }

  signSSO(returnUrl: string): Observable<any> {
    return this.http
      .get<{ redirectUrl: string }>(`${environment.api}/auth/ssoLogin`, {
        params: new HttpParams({
          fromString: stringify({ returnUrl }),
        }),
      })
      .pipe(
        map(
          (ret: { redirectUrl: string }) =>
            (window.location.href = ret.redirectUrl)
        )
      );
  }

  logout(): void {
    this.token.clear();
    this.router.navigateByUrl('/sign-in');
  }

  refreshToken(): Observable<Token> {
    const auth = this.token.getFullToken();
    const headers = {
      Authorization: `Bearer ${auth?.refresh_token}`,
    };
    return this.http
      .post<Token>(`${environment.api}/auth/refresh`, auth, { headers })
      .pipe(map((token) => token));
  }
}
